'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch } from '@/lib/api-fetch'
import { requestPayoutReportSync } from '@/lib/payout-report-sync'
import type {
  AnalyticsSummary,
  CustomerAnalytics,
} from '@/lib/analytics'
import type { ProductAnalyticsPublicDto } from '@/lib/product-analytics-storage'
import { selectCurrentMonthTurnover, type CurrentMonthTurnoverSelection } from '@/lib/current-month-turnover'
import { SettingsIcon } from '@/components/icons'
import { CustomerBadge } from '@/components/customers/CustomerBadge'
import { DeliveryStateBadge, DELIVERY_STATE_META } from '@/components/customers/DeliveryStateBadge'
import { ProductAnalyticsSection } from '@/components/analytics/ProductAnalyticsSection'
import { RevenueChart } from '@/components/analytics/RevenueChart'
import { PayoutTimeline } from '@/components/analytics/PayoutTimeline'
import { AnalyticsNow } from '@/components/analytics/AnalyticsNow'
import { ForecastsSection } from '@/components/analytics/ForecastsSection'
import { CustomerSortHeader } from '@/components/analytics/CustomerSortHeader'
import { CustomerKpiCards } from '@/components/analytics/CustomerKpiCards'
import { AnalyticsSection, chipClass } from '@/components/analytics/AnalyticsSection'
import { MarketMoneyPeriodPicker, presetLabel } from '@/components/analytics/AnalyticsPeriodPicker'
import { MarketMoneyPayoutsProvider, useMarketMoneyPayouts, useMarketMoneyReport } from '@/components/analytics/market-money-data'
import { MarketSummarySection } from '@/components/analytics/MarketSummarySection'
import { MarketPayoutsSection } from '@/components/analytics/MarketPayoutsSection'
import { ValueOrReason } from '@/components/warehouse/primitives'
import { moscowToday, pluralRu } from '@/lib/market-money'
import { compactCustomerName } from '@/lib/customer-name'
import { canonicalManufacturerKey, DEFAULT_MANUFACTURER_MARKUPS } from '@/lib/analytics-manufacturers'
import { fetchWithNetworkRetry } from '@/lib/client-fetch'
import { createLatestRequestGate } from '@/lib/latest-request'
import { buildMonthlyDynamics } from '@/lib/monthly-dynamics'
import {
  DEFAULT_CUSTOMER_SORT,
  nextCustomerSort,
  sortCustomers,
  type CustomerSort,
  type CustomerSortKey,
} from '@/lib/customer-sort'

interface AnalyticsResponse {
  analytics: AnalyticsSummary
  error?: string
}

const EMPTY: AnalyticsSummary = {
  generatedAt: '',
  period: {},
  totalRevenue: 0,
  estimatedProfit: 0,
  effectiveMargin: null,
  effectiveMarginAvailability: { availability: 'unavailable', reason: 'no_revenue' },
  totalOrders: 0,
  completedOrders: 0,
  averageCheck: null,
  averageCheckAvailability: { availability: 'unavailable', reason: 'no_orders' },
  customerCount: 0,
  repeatCustomerCount: 0,
  repeatRate: null,
  repeatRateAvailability: { availability: 'unavailable', reason: 'no_customers' },
  trends: {
    revenue: { current: 0, previous: 0, changePercent: 0, periodDays: 365, changeAvailability: 'available', changeReason: null },
    estimatedProfit: { current: 0, previous: 0, changePercent: 0, periodDays: 365, changeAvailability: 'available', changeReason: null },
    averageCheck: { current: null, previous: null, changePercent: null, periodDays: 30, availability: 'unavailable', reason: 'no_orders', previousAvailability: 'unavailable', previousReason: 'no_orders', changeAvailability: 'unavailable', changeReason: 'no_orders' },
    uniqueCustomers: { current: 0, previous: 0, changePercent: 0, periodDays: 30, changeAvailability: 'available', changeReason: null },
    repeatRate: {
      current: null,
      previous: null,
      changePercent: null,
      periodDays: 30,
      currentReturningCustomers: 0,
      previousReturningCustomers: 0,
      currentIdentifiedOrders: 0,
      previousIdentifiedOrders: 0,
      currentQualifyingOrders: 0,
      previousQualifyingOrders: 0,
      availability: 'unavailable',
      reason: 'no_customers',
      previousAvailability: 'unavailable',
      previousReason: 'no_customers',
      changeAvailability: 'unavailable',
      changeReason: 'no_customers',
    },
    matureRepeatRate: {
      current: null,
      previous: null,
      changePercent: null,
      periodDays: 30,
      currentMatureCustomers: 0,
      currentRepeatedCustomers: 0,
      previousMatureCustomers: 0,
      previousRepeatedCustomers: 0,
      availability: 'unavailable',
      reason: 'no_mature_customer_cohort',
      previousAvailability: 'unavailable',
      previousReason: 'no_mature_customer_cohort',
      changeAvailability: 'unavailable',
      changeReason: 'no_mature_customer_cohort',
    },
  },
  identifiedOrders: 0,
  coveragePercent: null,
  coveragePercentAvailability: { availability: 'unavailable', reason: 'no_eligible_orders' },
  p80Threshold: 0,
  monthly: [],
    materials: [],
    sources: [],
  returns: { source: 'ymarket-financial-report', monthly: [], baseline: null },
  availableManufacturers: [],
  config: { markups: { gold: 0, silver: 0, platinum: 0, other: 0 }, manufacturerMarkups: DEFAULT_MANUFACTURER_MARKUPS },
  payouts: {
    generatedAt: '', calculatorVersion: '', horizon: { from: '', to: '' }, months: [], weeklyBuckets: [],
    estimated: { amount: 0, orders: 0 },
    confirmed: { amount: null }, calibration: { status: 'unavailable', reason: '' },
    unallocated: { orders: 0, amount: 0 }, awaitingReconciliation: { orders: 0, amount: 0 }, weekdayTotals: [],
    reconciliation: {
      received: { status: 'unavailable', reason: '' },
      deliveredGross: { status: 'unavailable', reason: '' },
      transitPotential: { status: 'unavailable', reason: '' },
    },
  },
  customers: [],
}

const MATERIALS = [
  { key: 'gold', label: 'Золото', hint: 'Золото / Au' },
  { key: 'silver', label: 'Серебро', hint: 'Серебро / Ag' },
  { key: 'platinum', label: 'Платина', hint: 'Платина / Pt' },
  { key: 'other', label: 'Другое', hint: 'Не распознано' },
] as const

const CUSTOMERS_PER_PAGE = 20

const SECTION_NAV = [
  ['#summary', 'Сводка'],
  ['#payouts-section', 'Выплаты'],
  ['#trends', 'Динамика'],
  ['#products-section', 'Товары'],
  ['#forecasts-section', 'Прогнозы'],
  ['#refusals', 'Отказы'],
  ['#customers', 'Покупатели'],
] as const

function AnalyticsLoading() {
  return <div className="surface-card h-[300px] flex items-center justify-center text-ink-faint">Загрузка аналитики…</div>
}
const ANALYTICS_SESSION_CACHE_KEY = 'almaz:analytics-cache'
const ANALYTICS_SESSION_CACHE_PREFIX = `${ANALYTICS_SESSION_CACHE_KEY}:`

function removeVersionedAnalyticsCaches(): void {
  for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = sessionStorage.key(index)
    if (key?.startsWith(ANALYTICS_SESSION_CACHE_PREFIX)) sessionStorage.removeItem(key)
  }
}

function hasCompatiblePayoutCache(analytics: AnalyticsSummary): boolean {
  const payouts = analytics.payouts
  return Boolean(
    payouts &&
    Array.isArray(payouts.months) &&
    Array.isArray(payouts.weeklyBuckets) &&
    payouts.estimated &&
    payouts.unallocated &&
    payouts.awaitingReconciliation &&
    analytics.returns &&
    analytics.coveragePercentAvailability &&
    analytics.trends?.averageCheck?.availability &&
    analytics.trends?.repeatRate?.availability &&
    Array.isArray(analytics.returns.monthly) &&
    'baseline' in analytics.returns,
  )
}

function loadAnalyticsCache(): AnalyticsSummary | null {
  if (typeof window === 'undefined') return null
  try {
    removeVersionedAnalyticsCaches()
    const cached = JSON.parse(sessionStorage.getItem(ANALYTICS_SESSION_CACHE_KEY) || 'null') as { savedAt?: number; analytics?: AnalyticsSummary } | null
    if (
      !cached?.analytics ||
      !cached.savedAt ||
      Date.now() - cached.savedAt > 6 * 60 * 60_000 ||
      !hasCompatiblePayoutCache(cached.analytics)
    ) return null
    return cached.analytics
  } catch {
    return null
  }
}

function saveAnalyticsCache(analytics: AnalyticsSummary): void {
  try { sessionStorage.setItem(ANALYTICS_SESSION_CACHE_KEY, JSON.stringify({ savedAt: Date.now(), analytics })) } catch {}
}

function money(value: number): string {
  return `${Math.round(value).toLocaleString('ru-RU')} ₽`
}

function compactMoney(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} млн ₽`
  if (value >= 1_000) return `${Math.round(value / 1_000).toLocaleString('ru-RU')} тыс ₽`
  return money(value)
}

function date(value?: string): string {
  if (!value) return '—'
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit', month: 'short', year: 'numeric',
  }).format(new Date(value)).replace('.', '')
}

function syncStatus(value?: string): string {
  // The slot takes a time phrase; the absence is a different sentence, not a phrase.
  if (!value) return 'Данные заказов ещё не синхронизированы'
  return `Данные заказов синхронизированы ${relativeSync(value)}`
}

function relativeSync(value: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(value)) / 60_000))
  if (minutes < 1) return 'только что'
  if (minutes < 60) return `${minutes} мин назад`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} ч назад`
  return date(value)
}

function CustomerRow({ customer }: { customer: CustomerAnalytics }) {
  const [open, setOpen] = useState(false)
  const delivery = customer.delivery
  const deliveryMeta = delivery ? DELIVERY_STATE_META[delivery.state] : undefined
  return (
    <div className="border-b border-rule/70 last:border-b-0">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => (event.key === 'Enter' || event.key === ' ') && setOpen((value) => !value)}
        className="w-full grid grid-cols-[minmax(0,1fr)_20px] md:grid-cols-[minmax(220px,1.8fr)_100px_140px_140px_110px_20px] gap-3 md:gap-4 items-center px-4 md:px-5 py-4 text-left hover:bg-bone/35 transition-colors"
      >
        <span className="min-w-0">
          <span className="flex items-center gap-2 min-w-0">
            <CustomerBadge
              customerId={customer.id}
              name={customer.name}
              city={customer.city}
              phone={customer.phone}
              onNavigate={(event) => event.stopPropagation()}
            />
            {customer.isTop && (
              <span className="shrink-0 px-1.5 py-0.5 rounded-full bg-onyx text-ivory text-[8px] uppercase tracking-[0.1em] font-semibold">Топ 20%</span>
            )}
          </span>
          {customer.email && <span className="block mt-1 text-[10px] text-ink-faint truncate">{customer.email}</span>}
          <span className="md:hidden flex items-center gap-2 mt-2 text-[10px] text-ink-muted">
            <span>{customer.orderCount} зак.</span><span>·</span><span className="font-semibold text-ink tab-nums">{money(customer.totalSpent)}</span><span>·</span><span>{date(customer.lastOrderAt)}</span>
          </span>
        </span>
        <span className="hidden md:block text-[13px] text-ink tab-nums">{customer.orderCount}</span>
        <span className="hidden md:block text-[13px] font-semibold text-ink tab-nums">{money(customer.totalSpent)}</span>
        <span className="hidden md:block text-[12px] text-ink-muted tab-nums">{money(customer.averageCheck)}</span>
        <span className="hidden md:block text-[11px] text-ink-muted">{date(customer.lastOrderAt)}</span>
        <span className={`text-ink-faint transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
      </div>
      {open && (
        <div className="bg-bone/30 border-t border-rule/60 px-4 md:px-5 py-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4 max-w-xl">
            <div><div className="eyebrow">Первая покупка</div><div className="mt-1 text-[12px]">{date(customer.firstOrderAt)}</div></div>
            <div><div className="eyebrow">Доставлено</div><div className="mt-1 text-[12px] tab-nums">{customer.completedOrderCount}</div></div>
            <div><div className="eyebrow">Перцентиль</div><div className="mt-1 text-[12px] tab-nums">{customer.percentile}-й</div></div>
          </div>
          {delivery && (
            <div className="mb-4 border border-rule bg-paper p-4">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <div className="eyebrow">Состояние покупателя</div>
                  <div className="mt-1 text-[13px] font-semibold text-ink">{deliveryMeta?.label}</div>
                </div>
                <div className="text-[10px] text-ink-faint">изменено {date(delivery.changedAt)}</div>
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
                {delivery.shipments.map((shipment) => {
                  return (
                    <div key={shipment.cdekNumber} className="border border-rule/80 bg-ivory/50 p-3 text-[10px]">
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-semibold tab-nums">СДЭК {shipment.cdekNumber}</span>
                        <DeliveryStateBadge state={shipment.state} />
                      </div>
                      <div className="mt-2 text-ink-muted">
                        {[shipment.statusName, shipment.plannedDeliveryDate && `план ${date(shipment.plannedDeliveryDate)}`]
                          .filter(Boolean).join(' · ') || 'Данные отправления обновляются вебхуками'}
                      </div>
                      {shipment.courier?.phone && (
                        <div className="mt-2 font-medium text-ink">
                          Курьер {shipment.courier.name || ''} · {shipment.courier.phone}
                        </div>
                      )}
                      {shipment.agreement?.date && (
                        <div className="mt-1 text-ink-muted">
                          Договорённость: {date(shipment.agreement.date)} {shipment.agreement.timeFrom || ''}–{shipment.agreement.timeTo || ''}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              {delivery.recentTransitions.length > 0 && (
                <div className="mt-3 pt-3 border-t border-rule/70 flex flex-wrap gap-2">
                  {delivery.recentTransitions.slice(0, 6).map((transition) => (
                    <span key={transition.id} className="text-[9px] text-ink-faint">
                      {date(transition.occurredAt)} · {DELIVERY_STATE_META[transition.from].label} → {DELIVERY_STATE_META[transition.to].label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="space-y-1.5">
            {customer.orders.map((order) => (
              <div key={order.orderId} className="grid grid-cols-[1fr_auto] md:grid-cols-[130px_110px_1fr_110px] gap-2 md:gap-3 items-center bg-paper border border-rule px-3 py-2.5 text-[11px]">
                <span className="font-medium tab-nums">№{order.orderId}</span>
                <span className="text-ink-muted md:order-none order-3 col-span-2 md:col-span-1">{date(order.date)}</span>
                <span className="text-ink-muted truncate md:order-none order-4 col-span-2 md:col-span-1">{order.items.map((item) => item.name).join(', ') || 'Без состава'}</span>
                <span className="text-right font-semibold tab-nums">{money(order.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default function AnalyticsPage() {
  return <MarketMoneyPayoutsProvider><AnalyticsPageContent /></MarketMoneyPayoutsProvider>
}

function AnalyticsPageContent() {
  const [analytics, setAnalytics] = useState<AnalyticsSummary>(EMPTY)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [payoutRefreshing, setPayoutRefreshing] = useState(false)
  const [payoutRefreshMessage, setPayoutRefreshMessage] = useState('')
  const [query, setQuery] = useState('')
  const [topOnly, setTopOnly] = useState(false)
  const [metricMode, setMetricMode] = useState<'revenue' | 'profit'>('revenue')
  const [markupOpen, setMarkupOpen] = useState(false)
  const [savingMarkups, setSavingMarkups] = useState(false)
  const [markupDraft, setMarkupDraft] = useState(EMPTY.config.markups)
  const [manufacturerMarkupDraft, setManufacturerMarkupDraft] = useState(EMPTY.config.manufacturerMarkups)
  const [newManufacturer, setNewManufacturer] = useState('')
  const [productAnalytics, setProductAnalytics] = useState<ProductAnalyticsPublicDto | null>(null)
  const currentMonthTurnover = useMemo(
    () => selectCurrentMonthTurnover(productAnalytics, analytics.returns),
    [analytics.returns, productAnalytics],
  )
  const monthlyChart = useMemo(() => {
    return buildMonthlyDynamics(analytics.monthly, productAnalytics?.forecast.monthlyHorizon || [])
  }, [analytics.monthly, productAnalytics])
  const configuredManufacturers = useMemo(() => new Set(manufacturerMarkupDraft.map(entry => canonicalManufacturerKey(entry.name))), [manufacturerMarkupDraft])
  const availableManufacturers = analytics.availableManufacturers.filter(name => !configuredManufacturers.has(canonicalManufacturerKey(name)))
  const [customerPage, setCustomerPage] = useState(1)
  const [customerSort, setCustomerSort] = useState<CustomerSort>(DEFAULT_CUSTOMER_SORT)
  const analyticsRequestGate = useRef(createLatestRequestGate())
  const { report: marketReport, loading: marketLoading, error: marketError, period, pendingPreset, notice: periodNotice, changePeriod, reload: reloadMarketMoney } = useMarketMoneyReport()
  const { reload: reloadMarketPayouts } = useMarketMoneyPayouts()
  const [activeSection, setActiveSection] = useState<string>(SECTION_NAV[0][0])
  const scrollRootRef = useRef<HTMLDivElement>(null)
  const stickyBarRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async ({ quiet = false, fresh = false }: { quiet?: boolean; fresh?: boolean } = {}) => {
    const requestSequence = analyticsRequestGate.current.begin()
    if (!quiet) setLoading(true)
    setError('')
    try {
      const response = await fetchWithNetworkRetry(`/api/analytics${fresh ? '?fresh=1' : ''}`)
      const data = await response.json() as AnalyticsResponse
      if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`)
      if (!analyticsRequestGate.current.isCurrent(requestSequence)) return
      setAnalytics(data.analytics)
      saveAnalyticsCache(data.analytics)
    } catch (cause) {
      if (!analyticsRequestGate.current.isCurrent(requestSequence)) return
      const message = cause instanceof Error ? cause.message : ''
      setError(
        /network|fetch/i.test(message)
          ? 'Не удалось связаться с сервером аналитики. Проверьте подключение и обновите страницу.'
          : message || 'Не удалось загрузить аналитику',
      )
    } finally {
      if (!quiet && analyticsRequestGate.current.isCurrent(requestSequence)) setLoading(false)
    }
  }, [])

  useEffect(() => {
    const cached = loadAnalyticsCache()
    if (cached) {
      setAnalytics(cached)
      setLoading(false)
    }
    void load({ quiet: Boolean(cached) })
  }, [load])
  const refreshPayouts = useCallback(async () => {
    if (payoutRefreshing) return
    setPayoutRefreshing(true)
    setPayoutRefreshMessage('Проверяем готовность финансового отчёта Маркета…')
    try {
      const payload = await requestPayoutReportSync()
      // Payout sync writes a new financial aggregate. Do not immediately read
      // the short-lived analytics cache that may have been built before it.
      await load({ quiet: true, fresh: true })
      void reloadMarketMoney()
      void reloadMarketPayouts()
      setPayoutRefreshMessage(
        payload.status === 'downloaded'
          ? payload.settlementCount
            ? `Выплаты Маркета обновлены: ${payload.settlementCount} платёжных дней.`
            : 'Отчёт Маркета получен, но выплат в этом периоде не найдено.'
          : payload.status === 'failed'
            ? 'Маркет не сформировал отчёт. Нажмите «Обновить выплаты» ещё раз, чтобы запросить новый.'
          : 'Отчёт Маркета формируется. Страница проверит его снова через 30 минут; можно обновить вручную раньше.',
      )
    } catch (cause) {
      setPayoutRefreshMessage(cause instanceof Error ? cause.message : 'Не удалось обновить выплаты')
    } finally {
      setPayoutRefreshing(false)
    }
  }, [load, payoutRefreshing, reloadMarketMoney, reloadMarketPayouts])

  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') void refreshPayouts()
    }
    const timer = window.setInterval(refreshIfVisible, 30 * 60 * 1000)
    document.addEventListener('visibilitychange', refreshIfVisible)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', refreshIfVisible)
    }
  }, [refreshPayouts])
  useEffect(() => { setMarkupDraft(analytics.config.markups); setManufacturerMarkupDraft(analytics.config.manufacturerMarkups) }, [analytics.config])
  useEffect(() => { setCustomerPage(1) }, [query, topOnly, customerSort])
  useEffect(() => {
    const root = scrollRootRef.current
    const bar = stickyBarRef.current
    if (!root || !bar) return
    const applyOffset = () => root.style.setProperty('--analytics-sticky-offset', `${Math.ceil(bar.getBoundingClientRect().height)}px`)
    applyOffset()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(applyOffset)
    observer.observe(bar)
    return () => observer.disconnect()
  }, [])
  useEffect(() => {
    const root = scrollRootRef.current
    const bar = stickyBarRef.current
    if (typeof IntersectionObserver === 'undefined' || !root || !bar) return
    const visible = new Set<string>()
    const offset = Math.ceil(bar.getBoundingClientRect().height)
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.add(entry.target.id)
        else visible.delete(entry.target.id)
      }
      let chosen: HTMLElement | null = null
      for (const [href] of SECTION_NAV) {
        const element = document.getElementById(href.slice(1))
        if (!element || !visible.has(element.id)) continue
        // Refusals sits inside Товары; prefer the nested anchor while it is in view.
        if (chosen === null || chosen.contains(element)) chosen = element
      }
      if (chosen) setActiveSection(`#${chosen.id}`)
    }, { root, rootMargin: `-${offset + 12}px 0px -55% 0px` })
    for (const [href] of SECTION_NAV) {
      const element = document.getElementById(href.slice(1))
      if (element) observer.observe(element)
    }
    return () => observer.disconnect()
  }, [loading, productAnalytics])

  function chooseMetric(mode: 'revenue' | 'profit') {
    setMetricMode(mode)
    if (
      mode === 'profit' &&
      Object.values(analytics.config.markups).every((value) => value === 0) &&
      analytics.config.manufacturerMarkups.length === 0
    ) {
      setMarkupOpen(true)
    }
  }

  async function saveMarkups() {
    setSavingMarkups(true)
    setError('')
    try {
      const response = await apiFetch('/api/analytics/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ markups: markupDraft, manufacturerMarkups: manufacturerMarkupDraft }),
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`)
      await load({ fresh: true })
      setMarkupOpen(false)
      setMetricMode('profit')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось сохранить наценку')
    } finally {
      setSavingMarkups(false)
    }
  }

  const filteredCustomers = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('ru-RU')
    const matchingCustomers = analytics.customers.filter((customer) => {
      if (topOnly && !customer.isTop) return false
      if (!normalized) return true
      return [customer.name, customer.phone, customer.email, customer.city, customer.id]
        .filter(Boolean)
        .some((value) => value!.toLocaleLowerCase('ru-RU').includes(normalized))
    })
    return sortCustomers(matchingCustomers, customerSort)
  }, [analytics.customers, customerSort, query, topOnly])

  const changeCustomerSort = (key: CustomerSortKey) => {
    setCustomerSort((current) => nextCustomerSort(current, key))
  }

  const customerPageCount = Math.max(
    1,
    Math.ceil(filteredCustomers.length / CUSTOMERS_PER_PAGE)
  )
  const safeCustomerPage = Math.min(customerPage, customerPageCount)
  const pagedCustomers = filteredCustomers.slice(
    (safeCustomerPage - 1) * CUSTOMERS_PER_PAGE,
    safeCustomerPage * CUSTOMERS_PER_PAGE
  )

  const topCustomers = analytics.customers.filter((customer) => customer.isTop).slice(0, 5)
  const maxTop = Math.max(...topCustomers.map((customer) => customer.totalSpent), 1)

  return (
    <div ref={scrollRootRef} className="h-full overflow-y-auto bg-ivory">
      <div className="max-w-[1380px] mx-auto px-3 sm:px-6 md:px-8 xl:px-12 py-4 sm:py-7 xl:py-10 pb-[calc(24px+env(safe-area-inset-bottom))]">
        <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-6 mb-4 md:mb-8">
          <div>
            <div className="eyebrow mb-1.5 sm:mb-3">Продажи · деньги · ассортимент · клиенты</div>
            <h1 className="text-[23px] sm:text-[30px] md:text-[40px] font-semibold text-ink tracking-[-0.02em] leading-tight md:leading-none truncate">Аналитика</h1>
            <div className="mt-1.5 flex items-center gap-1.5 sm:mt-3">
              <p className="text-[10px] sm:text-[12px] text-ink-muted">
                {syncStatus(analytics.lastSyncedAt)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <Link
              to="/analytics/unit-economics"
              className="inline-flex h-[35px] items-center border border-rule-strong bg-paper px-3 text-[13px] text-ink-2 transition-colors hover:text-ink"
            >
              Юнит-экономика
            </Link>
            <button
              onClick={() => setMarkupOpen((value) => !value)}
              className={`h-[35px] w-[35px] inline-flex items-center justify-center border transition-colors ${markupOpen ? 'border-onyx bg-onyx text-ivory' : 'border-rule-strong bg-paper text-ink-muted hover:text-ink'}`}
              title="Настроить наценку"
              aria-label="Настроить наценку"
            >
              <SettingsIcon />
            </button>
          </div>
        </header>

        {/* Fixed height per breakpoint: nothing inside (pill, picker, notice) may resize the band or move the tabs. */}
        <div ref={stickyBarRef} data-testid="analytics-period-bar" className="sticky top-0 z-30 flex h-[104px] flex-col justify-center gap-2 border-t-2 border-b border-t-ink border-b-rule bg-paper/95 px-3 backdrop-blur sm:px-4 lg:h-[60px] lg:flex-row lg:items-center lg:justify-between lg:gap-6">
          <nav aria-label="Разделы аналитики" className="flex h-9 min-w-0 max-w-full shrink-0 items-center gap-[22px] overflow-x-auto overflow-y-hidden text-[13px] text-ink-muted [scrollbar-width:none]">
            {SECTION_NAV.map(([href, label]) => (
              <a
                key={href}
                href={href}
                aria-current={activeSection === href ? 'true' : undefined}
                onClick={() => setActiveSection(href)}
                className={`shrink-0 border-b-2 pb-[3px] transition-colors hover:text-ink ${activeSection === href ? 'border-gold font-semibold text-ink' : 'border-transparent'}`}
              >
                {label}
              </a>
            ))}
          </nav>
          <div data-testid="analytics-period-controls" className="flex h-9 min-w-0 max-w-full items-center lg:justify-end">
            {period ? (
              <MarketMoneyPeriodPicker
                value={period}
                today={moscowToday()}
                onChange={changePeriod}
              />
            ) : (
              <span role="status" className="flex h-9 min-w-0 items-center gap-2 text-[12px] text-ink-faint">
                <span>{marketError ? 'Период недоступен' : 'Загружаем период…'}</span>
                {pendingPreset && <span className="rounded-full bg-bone px-2 py-0.5 text-[11px] font-semibold text-gold-deep">{presetLabel(pendingPreset).toLocaleLowerCase('ru-RU')}</span>}
              </span>
            )}
          </div>
        </div>

        <p data-testid="period-notice-slot" aria-live="polite" title={periodNotice ?? undefined} className="mb-4 mt-1.5 h-5 truncate text-[12px] leading-5 text-gold-deep md:mb-7">
          {periodNotice && <span data-testid="period-notice">{periodNotice}</span>}
        </p>

        {error && (
          <div className="mb-6 px-4 py-3 bg-st-unpaid/8 border border-st-unpaid/25 text-st-unpaid text-[13px]">{error}</div>
        )}

        {markupOpen && (
          <section className="surface-card mb-6 p-5 border-t-2 !border-t-gold">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 sm:gap-6 mb-5">
              <div>
                <div className="eyebrow">Расчёт дохода</div>
                <h2 className="mt-1 text-[17px] font-semibold">Наценка по производителю и металлу</h2>
                <p className="mt-1.5 text-[11px] text-ink-muted">Сначала применяется наценка производителя из каталога Яндекса. Если производителя нет в списке — используется металл. 100% наценки = 50% оборота прибылью. Настройки сохраняются на сервере.</p>
              </div>
              <button onClick={() => setMarkupOpen(false)} className="text-ink-faint hover:text-ink text-[20px] leading-none">×</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {MATERIALS.map((material) => {
                const stats = analytics.materials.find((entry) => entry.material === material.key)
                return (
                  <label key={material.key} className="border border-rule bg-ivory/50 p-3 flex items-center justify-between gap-3">
                    <span>
                      <span className="block text-[12px] font-semibold text-ink">{material.label}</span>
                      <span className="block mt-0.5 text-[9px] text-ink-faint">{material.hint} · {compactMoney(stats?.revenue || 0)}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        max="1000"
                        step="0.1"
                        value={markupDraft[material.key]}
                        onChange={(event) => setMarkupDraft((current) => ({
                          ...current,
                          [material.key]: Math.min(1000, Math.max(0, Number(event.target.value) || 0)),
                        }))}
                        className="input !w-[72px] text-right tab-nums"
                      />
                      <span className="text-[11px] text-ink-muted">%</span>
                    </span>
                  </label>
                )
              })}
            </div>
            <div className="mt-6 border-t border-rule pt-5">
              <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                <div><h3 className="text-[14px] font-semibold">Производители</h3><p className="mt-0.5 text-[10px] text-ink-faint">Название сопоставляется без учёта регистра, ООО/АО и кириллицы/латиницы.</p></div>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {manufacturerMarkupDraft.map((entry, index) => (
                  <div key={`${entry.name}-${index}`} className="flex items-center gap-2 border border-rule bg-ivory/50 p-2.5">
                    <span className="min-w-0 flex-1 truncate px-1 text-[12px] font-semibold" title={entry.name}>{entry.name}</span>
                    <input aria-label={`Наценка ${entry.name}`} type="number" min="0" max="1000" step="0.1" value={entry.markup} onChange={event => setManufacturerMarkupDraft(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, markup: Math.min(1000, Math.max(0, Number(event.target.value) || 0)) } : item))} className="input !w-[72px] text-right tab-nums" />
                    <span className="text-[11px] text-ink-muted">%</span>
                    <button type="button" aria-label={`Удалить ${entry.name}`} onClick={() => setManufacturerMarkupDraft(current => current.filter((_, itemIndex) => itemIndex !== index))} className="grid size-8 shrink-0 place-items-center text-lg text-ink-faint hover:text-st-unpaid">×</button>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <select aria-label="Выбрать производителя" value={newManufacturer} onChange={event => setNewManufacturer(event.target.value)} className="input w-full sm:max-w-xs">
                  <option value="">Выберите производителя из каталога</option>
                  {availableManufacturers.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
                <button type="button" disabled={!newManufacturer.trim()} onClick={() => { setManufacturerMarkupDraft(current => [...current, { name: newManufacturer.trim(), markup: 0 }]); setNewManufacturer('') }} className="btn btn-ghost">+ Добавить производителя</button>
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="text-[12px] text-ink-muted">
                Текущая оценка дохода: <span className="font-semibold text-ink tab-nums">{money(analytics.estimatedProfit)}</span>
                <span className="mx-2 text-rule-strong">·</span>
                после сохранения будет пересчитана по новым правилам
              </div>
              <button onClick={saveMarkups} disabled={savingMarkups} className="btn btn-primary">
                {savingMarkups ? 'Сохраняем…' : 'Сохранить и рассчитать'}
              </button>
            </div>
          </section>
        )}

        <MarketSummarySection report={marketReport} loading={marketLoading} error={marketError}>
          {loading
            ? <AnalyticsLoading />
            : <AnalyticsNow products={productAnalytics} selection={currentMonthTurnover} />}
        </MarketSummarySection>

        <MarketPayoutsSection
          report={marketReport}
          loading={marketLoading}
          error={marketError}
          refreshing={payoutRefreshing}
          onRefreshPayouts={() => { void refreshPayouts() }}
          timeline={
            <PayoutTimeline
              projection={analytics.payouts}
              loading={loading}
              refreshMessage={payoutRefreshMessage}
              lastCheckedAt={analytics.payoutsLastCheckedAt}
              reportStatus={analytics.payoutsReportStatus}
            />
          }
        />

        <AnalyticsSection
          id="trends"
          index="03"
          eyebrow="Динамика"
          title={`${metricMode === 'profit' ? 'Доход (оценка)' : 'Оборот'} по месяцам`}
          aside={
            <div className="flex flex-wrap gap-2" role="group" aria-label="Вид графика">
              <button type="button" aria-pressed={metricMode === 'revenue'} onClick={() => chooseMetric('revenue')} className={chipClass(metricMode === 'revenue')}>Оборот</button>
              <button type="button" aria-pressed={metricMode === 'profit'} onClick={() => chooseMetric('profit')} className={chipClass(metricMode === 'profit')}>Доход (оценка)</button>
            </div>
          }
        >
          {loading ? <AnalyticsLoading /> : (
            <article className="surface-card px-5 pt-5 pb-2">
              <div className="mb-2 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div><div className="eyebrow">По дате заказа</div><h3 className="mt-1 text-[17px] font-semibold">{metricMode === 'profit' ? 'Доход (оценка)' : 'Оборот'} по месяцам</h3></div>
                <div className="text-right"><div className="text-[11px] text-ink-faint">{pluralRu(monthlyChart.completed.length, ['завершённый месяц', 'завершённых месяца', 'завершённых месяцев'])}</div><div className="mt-1 text-[12px] font-medium tab-nums">{compactMoney(monthlyChart.completed.reduce((sum, point) => sum + (metricMode === 'profit' ? point.profit : point.revenue), 0))}</div></div>
              </div>
              <RevenueChart points={monthlyChart.completed} mode={metricMode} forecasts={monthlyChart.horizon} current={monthlyChart.current} />
            </article>
          )}
        </AnalyticsSection>

        <AnalyticsSection id="products-section" index="04" eyebrow="Товары" title="Ассортимент и спрос">
          <ProductAnalyticsSection embedded range={period?.range ?? null} onData={setProductAnalytics} />
        </AnalyticsSection>

        <AnalyticsSection id="forecasts-section" index="05" eyebrow="Прогнозы" title="Планирование">
          {loading ? <AnalyticsLoading /> : <ForecastsSection embedded products={productAnalytics} returns={analytics.returns} />}
        </AnalyticsSection>

        <AnalyticsSection id="customers" index="06" eyebrow="Покупатели" title="Клиентские показатели · все источники">
          {loading ? <AnalyticsLoading /> : (
            <div>
              <CustomerKpiCards trends={analytics.trends} />
              <article aria-label="Топ покупателей" className="surface-card mb-3 p-5">
                <div className="eyebrow">80-й перцентиль</div>
                <div className="mt-1 flex items-baseline justify-between gap-3">
                  <h2 className="text-[17px] font-semibold">Топ покупателей · вся история</h2>
                  <span className="text-[11px] text-ink-faint tab-nums">от {money(analytics.p80Threshold)}</span>
                </div>
                <div className="mt-6 space-y-4">
                  {topCustomers.length === 0 && <div className="py-12 text-center text-[12px] text-ink-faint">Пока недостаточно данных</div>}
                  {topCustomers.map((customer, index) => (
                    <div key={customer.id}>
                      <div className="flex items-center justify-between gap-3 text-[11px] mb-1.5">
                        <Link to={`/customers/${encodeURIComponent(customer.id)}`} className="truncate hover:text-gold-deep">
                          <span className="text-ink-faint mr-2 tab-nums">{String(index + 1).padStart(2, '0')}</span>{compactCustomerName(customer.name)}
                        </Link>
                        <span className="font-semibold tab-nums shrink-0">{money(customer.totalSpent)}</span>
                      </div>
                      <div className="h-1 bg-bone overflow-hidden"><div className="h-full bg-gold" style={{ width: `${(customer.totalSpent / maxTop) * 100}%` }} /></div>
                    </div>
                  ))}
                </div>
              </article>
              <div className="mb-3 flex items-center justify-between gap-3 border border-rule bg-paper px-3 py-2 text-[9px] text-ink-muted sm:px-4 sm:text-[11px]">
                <span className="leading-snug">Показатели покупателей учитывают все источники. В списке ниже — только покупатели Яндекс Маркета; покупателей разных площадок мы показываем отдельно.</span>
                <span className="shrink-0 font-medium tab-nums text-ink">Покрытие <ValueOrReason value={analytics.coveragePercentAvailability.availability === 'available' && analytics.coveragePercent !== null ? `${analytics.coveragePercent.toFixed(0)}%` : null} reason={null} message="Нет заказов Маркета" /></span>
              </div>
              <div className="surface-card">
              <div className="p-4 md:p-5 border-b border-rule flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-5">
                <div>
                  <div className="eyebrow">Список покупателей</div>
                  <h2 className="mt-1 text-[19px] font-semibold">Все покупатели Маркета</h2>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button onClick={() => setTopOnly((value) => !value)} className={`btn btn-sm ${topOnly ? 'btn-primary' : 'btn-ghost'}`}>Топ 20%</button>
                  <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Имя, город, почта…" className="input flex-1 sm:w-[240px]" />
                </div>
              </div>
              <div className="hidden md:grid grid-cols-[minmax(220px,1.8fr)_100px_140px_140px_110px_20px] gap-4 px-5 py-2.5 border-b border-rule bg-bone/35 text-[9px] uppercase tracking-[0.1em] font-semibold text-ink-faint">
                <span className="flex items-center">Покупатель</span>
                <CustomerSortHeader label="Заказы" sortKey="orders" sort={customerSort} onSort={changeCustomerSort} />
                <CustomerSortHeader label="Всего" sortKey="total" sort={customerSort} onSort={changeCustomerSort} />
                <CustomerSortHeader label="Средний чек" sortKey="averageCheck" sort={customerSort} onSort={changeCustomerSort} />
                <CustomerSortHeader label="Последний" sortKey="lastOrder" sort={customerSort} onSort={changeCustomerSort} />
                <span />
              </div>
              <div>
                {pagedCustomers.map((customer) => <CustomerRow key={customer.id} customer={customer} />)}
                {filteredCustomers.length === 0 && <div className="py-16 text-center text-[13px] text-ink-faint">Покупатели не найдены</div>}
              </div>
              {filteredCustomers.length > 0 && (
                <div className="px-5 py-3.5 border-t border-rule flex items-center justify-between gap-4 bg-bone/20">
                  <span className="text-[11px] text-ink-faint tab-nums">
                    {(safeCustomerPage - 1) * CUSTOMERS_PER_PAGE + 1}–{Math.min(safeCustomerPage * CUSTOMERS_PER_PAGE, filteredCustomers.length)} из {filteredCustomers.length}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCustomerPage((page) => Math.max(1, page - 1))}
                      disabled={safeCustomerPage === 1}
                      className="btn btn-sm btn-ghost disabled:opacity-35"
                    >
                      Назад
                    </button>
                    {Array.from({ length: customerPageCount }, (_, index) => index + 1)
                      .filter((page) =>
                        page === 1 ||
                        page === customerPageCount ||
                        Math.abs(page - safeCustomerPage) <= 1
                      )
                      .map((page, index, visiblePages) => (
                        <span key={page} className="flex items-center gap-1">
                          {index > 0 && page - visiblePages[index - 1] > 1 && (
                            <span className="px-1 text-ink-faint">…</span>
                          )}
                          <button
                            onClick={() => setCustomerPage(page)}
                            className={`size-[30px] text-[11px] border transition-colors ${
                              page === safeCustomerPage
                                ? 'bg-onyx border-onyx text-ivory'
                                : 'bg-paper border-rule-strong text-ink-muted hover:text-ink'
                            }`}
                          >
                            {page}
                          </button>
                        </span>
                      ))}
                    <button
                      onClick={() => setCustomerPage((page) => Math.min(customerPageCount, page + 1))}
                      disabled={safeCustomerPage === customerPageCount}
                      className="btn btn-sm btn-ghost disabled:opacity-35"
                    >
                      Далее
                    </button>
                  </div>
                </div>
              )}
              </div>
            </div>
          )}
        </AnalyticsSection>
      </div>
    </div>
  )
}
